// hooks/useStructuredData.js
// 🎯 Hook para generar Structured Data (JSON-LD) automáticamente para SEO
import { useEffect } from "react";

const SITE_NAME = "Tattersall Venta Usados";
const SITE_URL = "https://tattersallventausados.cl";
const LOGO_URL = `${SITE_URL}/favicon.png`;

/**
 * 🏢 Schema de la Organización (se usa en todas las páginas)
 */
const getOrganizationSchema = () => ({
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Tattersall Gestión de Activos",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: LOGO_URL,
    width: 200,
    height: 60,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+56-2-2828-7700",
      contactType: "sales",
      areaServed: "CL",
      availableLanguage: "Spanish",
    },
    {
      "@type": "ContactPoint",
      telephone: "+56-9-3176-4533",
      contactType: "customer service",
      areaServed: "CL",
      availableLanguage: "Spanish",
    },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Camino Longitudinal, Ruta 5 2535",
    addressLocality: "Buin",
    addressRegion: "Región Metropolitana",
    postalCode: "",
    addressCountry: "CL",
  },
  sameAs: [
    "https://www.linkedin.com/company/tattersall-gda/",
    "https://www.facebook.com/tattersallgda",
  ],
});

/**
 * 🌐 Schema del Website
 */
const getWebsiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  description: "Venta de vehículos, maquinaria y equipos usados en Chile",
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/catalogo?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/**
 * 🛒 Schema de Producto
 */
const getProductSchema = (product) => {
  if (!product) return null;

  // Extraer precio numérico
  const extractPrice = (priceStr) => {
    if (!priceStr) return null;
    const numericPrice = priceStr.toString().replace(/[^\d]/g, "");
    return numericPrice ? parseInt(numericPrice) : null;
  };

  const price = extractPrice(product.price || product.formattedPrice);
  const images =
    product.images?.length > 0
      ? product.images
      : product.image
      ? [product.image]
      : [];

  // Determinar disponibilidad
  const availability = product.is_sold
    ? "https://schema.org/SoldOut"
    : "https://schema.org/InStock";

  // Determinar condición del item
  const itemCondition =
    product.condition === "Nuevo" || product.estado === "nuevo"
      ? "https://schema.org/NewCondition"
      : "https://schema.org/UsedCondition";

  const productSchema = {
    "@type": "Product",
    "@id": `${SITE_URL}/producto/${product.slug}#product`,
    name: product.name,
    description:
      product.description ||
      `${product.name} - ${
        product.category || "Vehículo/Maquinaria"
      } usado en venta`,
    url: `${SITE_URL}/producto/${product.slug}`,
    image: images,
    brand: product.brand
      ? {
          "@type": "Brand",
          name: product.brand,
        }
      : undefined,
    model: product.model || undefined,
    category: product.category || undefined,
    manufacturer: product.brand
      ? {
          "@type": "Organization",
          name: product.brand,
        }
      : undefined,
    itemCondition,
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/producto/${product.slug}`,
      priceCurrency: "CLP",
      price: price || undefined,
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0], // 30 días
      availability,
      seller: {
        "@id": `${SITE_URL}/#organization`,
      },
      itemCondition,
    },
  };

  // Agregar propiedades adicionales si existen
  if (product.year) {
    productSchema.productionDate = product.year.toString();
    productSchema.vehicleModelDate = product.year.toString();
  }

  if (product.sku || product.id) {
    productSchema.sku = product.sku || product.id.toString();
  }

  // Para vehículos, agregar schema específico
  if (isVehicle(product.category)) {
    return getVehicleSchema(product, productSchema);
  }

  return productSchema;
};

/**
 * 🚗 Schema específico para Vehículos
 */
const getVehicleSchema = (product, baseSchema) => {
  const vehicleSchema = {
    ...baseSchema,
    "@type": "Vehicle",
    vehicleConfiguration: product.model || undefined,
    vehicleModelDate: product.year?.toString() || undefined,
    mileageFromOdometer:
      product.kilometers || product.usage
        ? {
            "@type": "QuantitativeValue",
            value: parseInt(
              (product.kilometers || product.usage || "0")
                .toString()
                .replace(/[^\d]/g, "")
            ),
            unitCode: product.usageUnit === "hr" ? "HUR" : "KMT",
          }
        : undefined,
    fuelType: product.combustible || undefined,
    vehicleTransmission: product.transmision || undefined,
    color: product.color || undefined,
    numberOfDoors: product.puertas ? parseInt(product.puertas) : undefined,
    vehicleSeatingCapacity: product.asientos
      ? parseInt(product.asientos)
      : undefined,
    vehicleEngine: product.motor
      ? {
          "@type": "EngineSpecification",
          name: product.motor,
        }
      : undefined,
  };

  // Limpiar undefined
  return cleanUndefined(vehicleSchema);
};

/**
 * 📋 Schema de Lista de Productos (para catálogo)
 */
const getProductListSchema = (products, listName = "Catálogo de Productos") => {
  if (!products || products.length === 0) return null;

  return {
    "@type": "ItemList",
    name: listName,
    numberOfItems: products.length,
    itemListElement: products.slice(0, 20).map((product, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Product",
        name: product.name,
        url: `${SITE_URL}/producto/${product.slug}`,
        image: product.image || product.images?.[0],
        offers: {
          "@type": "Offer",
          priceCurrency: "CLP",
          price:
            parseInt((product.price || "0").toString().replace(/[^\d]/g, "")) ||
            undefined,
          availability: product.is_sold
            ? "https://schema.org/SoldOut"
            : "https://schema.org/InStock",
        },
      },
    })),
  };
};

/**
 * 🍞 Schema de Breadcrumb
 */
const getBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url ? `${SITE_URL}${crumb.url}` : undefined,
    })),
  };
};

/**
 * ❓ Schema de FAQ
 */
const getFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

// ============ UTILIDADES ============

/**
 * Determinar si una categoría es un vehículo
 */
const isVehicle = (category) => {
  if (!category) return false;
  const vehicleCategories = [
    "camion",
    "camioneta",
    "automovil",
    "auto",
    "vehiculo",
    "bus",
    "minibus",
    "furgon",
    "station wagon",
    "jeep",
    "tracto",
    "remolque",
  ];
  return vehicleCategories.some((v) => category.toLowerCase().includes(v));
};

/**
 * Limpiar propiedades undefined de un objeto
 */
const cleanUndefined = (obj) => {
  const cleaned = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "object" && !Array.isArray(value)) {
        const cleanedNested = cleanUndefined(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else {
        cleaned[key] = value;
      }
    }
  });
  return cleaned;
};

/**
 * Inyectar JSON-LD en el DOM
 */
const injectJsonLd = (schemas, id = "structured-data") => {
  // Remover script anterior si existe
  const existingScript = document.getElementById(id);
  if (existingScript) {
    existingScript.remove();
  }

  if (!schemas || schemas.length === 0) return;

  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";

  // Crear el graph con todos los schemas
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": schemas.filter(Boolean).map(cleanUndefined),
  };

  script.textContent = JSON.stringify(jsonLd, null, 0);
  document.head.appendChild(script);

  console.log("📊 [SEO] Structured Data inyectado:", schemas.length, "schemas");
};

// ============ HOOKS ============

/**
 * 🎣 Hook principal para Structured Data
 */
export const useStructuredData = ({
  type = "website",
  product = null,
  products = null,
  breadcrumbs = null,
  faqs = null,
  customSchemas = [],
}) => {
  useEffect(() => {
    const schemas = [getOrganizationSchema(), getWebsiteSchema()];

    // Agregar schema según el tipo de página
    switch (type) {
      case "product":
        if (product) {
          schemas.push(getProductSchema(product));
          // Agregar breadcrumb para producto
          schemas.push(
            getBreadcrumbSchema([
              { name: "Inicio", url: "/" },
              { name: "Catálogo", url: "/catalogo" },
              { name: product.name },
            ])
          );
        }
        break;

      case "catalog":
        if (products) {
          schemas.push(getProductListSchema(products));
        }
        if (breadcrumbs) {
          schemas.push(getBreadcrumbSchema(breadcrumbs));
        }
        break;

      case "faq":
        if (faqs) {
          schemas.push(getFAQSchema(faqs));
        }
        break;

      default:
        // Para homepage y otras páginas, solo org + website
        break;
    }

    // Agregar schemas personalizados
    if (customSchemas.length > 0) {
      schemas.push(...customSchemas);
    }

    injectJsonLd(schemas);

    // Cleanup al desmontar
    return () => {
      const script = document.getElementById("structured-data");
      if (script) {
        script.remove();
      }
    };
  }, [type, product, products, breadcrumbs, faqs, customSchemas]);
};

/**
 * 🎣 Hook específico para página de producto
 */
export const useProductStructuredData = (product) => {
  useStructuredData({
    type: "product",
    product,
  });
};

/**
 * 🎣 Hook específico para catálogo
 */
export const useCatalogStructuredData = (products, breadcrumbs = null) => {
  useStructuredData({
    type: "catalog",
    products,
    breadcrumbs,
  });
};

export default useStructuredData;
